import { useTheme } from "@mui/material"
import React, { useEffect, useMemo, useRef, useState } from "react"
import type { LineI } from "tweenly"
import { renderStyles } from "tweenly"
import { suppressNextClick } from "../../../helpers/dragClick.helpers"
import {
    PointT,
    applyLineFrame,
    getLineEndpoints,
    getLineFrame,
    lineFrameFromEndpoints,
    snapTo45,
} from "../../../helpers/line.helpers"
import { createResizePreview, ResizePreviewT } from "../../../helpers/resizePreview.helpers"
import { createResizeSnapSession, ResizeSnapSession } from "../../../helpers/snapSession.helpers"
import {
    ObjectsById,
    domObjectSize,
    getAbsoluteObjectPosition,
} from "../../../helpers/wrapper.helpers"
import { RESIZE_HANDLE_ATTRIBUTE, setGlobalCursor } from "../../../hooks/editor/useHoldDrag"
import useDynamicRefs from "../../../hooks/useDynamicRefs"
import { useAppDispatch, useAppStore } from "../../../hooks/useRedux"
import { updateObjectsPositionAction } from "../../../store/slices/objects.slice"
import { CORNER_SIZE_PX } from "./DraggableItem"

type Props = {
    object: LineI
    objectsById: ObjectsById
    /** canvas units → screen px */
    scale: number
    /** top-left of the selection overlay these are drawn in, absolute canvas units */
    origin: { top: number; left: number }
    /** false for a locked / hidden line: outlined, but its ends cannot be dragged */
    interactive: boolean
}

type Gesture = {
    pointerId: number
    /** which end is dragged: 0 = start, 1 = end */
    index: 0 | 1
    startClientX: number
    startClientY: number
    lastClientX: number
    lastClientY: number
    /** both end points when the gesture started, parent coordinates */
    points: [PointT, PointT]
    /** parent coordinates → absolute canvas coordinates, where the snap targets live */
    parentOffset: PointT
    /** snap to objects — null when it is switched off */
    snap: ResizeSnapSession | null
    preview: ResizePreviewT
    /** the line's element, taken when the gesture started */
    element: HTMLElement | null
    /** the element's inline transform before the preview touched it */
    originalTransform: string
    last: { left: number; top: number; width: number; rotation: number } | null
}

/**
 * Put the line and the document back the way the gesture found them. Works off the gesture
 * alone, so the unmount cleanup cannot pick up a stale render's object.
 */
const restoreGesture = (gesture: Gesture) => {
    gesture.preview.restore()
    gesture.snap?.end()
    if (gesture.element) gesture.element.style.transform = gesture.originalTransform
    document.body.style.userSelect = ""
    setGlobalCursor(null)
}

/**
 * The selection of a single line, the way Figma draws it: the line itself outlined and a
 * handle on each end. Dragging an end moves just that end — the other stays put — so one
 * gesture changes length and angle together. Shift keeps the line on 45° steps.
 *
 * The geometry lives in line.helpers; this only turns pointer moves into end points, previews
 * them on the element (like a resize, no store write per move) and commits once on release,
 * styles and rotation in a single undo step.
 */
const LineHandles = ({ object, objectsById, scale, origin, interactive }: Props) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const store = useAppStore()
    const [getRef] = useDynamicRefs<HTMLElement>()
    const gestureRef = useRef<Gesture | null>(null)
    const [livePoints, setLivePoints] = useState<[PointT, PointT] | null>(null)

    const frame = useMemo(() => getLineFrame(object), [object])

    /** parent coordinates → absolute canvas coordinates */
    const parentOffset = useMemo(() => {
        const absolute = getAbsoluteObjectPosition(object.id, objectsById, domObjectSize)
        return { x: absolute.left - frame.left, y: absolute.top - frame.top }
    }, [object.id, objectsById, frame])

    const points = livePoints ?? getLineEndpoints(frame)
    // drawn relative to the overlay box
    const [start, end] = points.map((point) => ({
        x: point.x + parentOffset.x - origin.left,
        y: point.y + parentOffset.y - origin.top,
    }))

    /**
     * Move the dragged end to the pointer and preview the line.
     *
     * The end snaps to other objects as a point — onto their edges and centres, x and y on
     * their own. Not under Shift: the 45° step would pull it straight off the snapped spot.
     * Ctrl suspends snapping, as everywhere else.
     */
    const preview = (
        gesture: Gesture,
        clientX: number,
        clientY: number,
        shiftKey: boolean,
        ctrlKey: boolean
    ) => {
        gesture.lastClientX = clientX
        gesture.lastClientY = clientY
        const anchor = gesture.points[gesture.index === 0 ? 1 : 0]
        const moved = gesture.points[gesture.index]
        const { parentOffset: offset } = gesture
        const pointerDelta = {
            x: (clientX - gesture.startClientX) / scale,
            y: (clientY - gesture.startClientY) / scale,
        }
        const delta = gesture.snap
            ? gesture.snap.snap(
                  { x: moved.x + offset.x, y: moved.y + offset.y },
                  pointerDelta,
                  shiftKey || ctrlKey
              ).delta
            : pointerDelta
        let point = { x: moved.x + delta.x, y: moved.y + delta.y }
        if (shiftKey) point = snapTo45(anchor, point)
        // a line is never dragged down to nothing — it could not be picked up again
        if (Math.hypot(point.x - anchor.x, point.y - anchor.y) < 1) return
        gesture.snap?.showGuides({
            left: point.x + offset.x,
            top: point.y + offset.y,
            width: 0,
            height: 0,
        })

        const next =
            gesture.index === 0
                ? lineFrameFromEndpoints(frame, point, anchor)
                : lineFrameFromEndpoints(frame, anchor, point)
        if (!next) return
        gesture.last = next

        const { element } = gesture
        if (element) {
            gesture.preview.apply(element, { left: next.left, top: next.top, width: next.width })
            const { transforms } = applyLineFrame(object.styles, object.transforms, next)
            const rendered = renderStyles(
                { ...object, transforms },
                "inline"
            ) as React.CSSProperties
            element.style.transform = String(rendered.transform ?? "")
        }
        setLivePoints(gesture.index === 0 ? [point, anchor] : [anchor, point])
    }

    const finish = (commit: boolean) => {
        const gesture = gestureRef.current
        if (!gesture) return
        gestureRef.current = null
        restoreGesture(gesture)
        setLivePoints(null)
        if (!commit || !gesture.last) return

        // released off the handle, the click would land on whatever is underneath
        suppressNextClick()
        const { styles, transforms } = applyLineFrame(
            object.styles,
            object.transforms,
            gesture.last
        )
        dispatch(
            updateObjectsPositionAction([
                { object, property: "styles", value: styles },
                { object, property: "transforms", value: transforms },
            ])
        )
    }

    const handlePointerDown = (index: 0 | 1) => (event: React.PointerEvent<HTMLDivElement>) => {
        if (!interactive || event.button !== 0) return
        // the canvas below must neither start a move nor see a click that deselects
        event.stopPropagation()
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        const element = getRef(object.id)?.current ?? null
        gestureRef.current = {
            pointerId: event.pointerId,
            index,
            startClientX: event.clientX,
            startClientY: event.clientY,
            lastClientX: event.clientX,
            lastClientY: event.clientY,
            points: getLineEndpoints(frame),
            parentOffset,
            // measured here, before the preview starts rewriting the line's DOM
            snap: store.getState().editor.value.settings.canvasSnapToObjectsOn
                ? createResizeSnapSession([object.id], objectsById)
                : null,
            preview: createResizePreview(),
            element,
            originalTransform: element?.style.transform ?? "",
            last: null,
        }
        document.body.style.userSelect = "none"
        setGlobalCursor("crosshair")
    }

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const gesture = gestureRef.current
        if (!gesture || event.pointerId !== gesture.pointerId) return
        preview(gesture, event.clientX, event.clientY, event.shiftKey, event.ctrlKey)
    }

    // worked out again from the release point — in a quick flick the moves fall behind
    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        const gesture = gestureRef.current
        if (!gesture || event.pointerId !== gesture.pointerId) return
        preview(gesture, event.clientX, event.clientY, event.shiftKey, event.ctrlKey)
        finish(true)
    }

    useEffect(() => {
        const handleKey = (event: KeyboardEvent) => {
            const gesture = gestureRef.current
            if (!gesture) return
            // Escape abandons the drag, the line goes back to where it was
            if (event.key === "Escape" && event.type === "keydown") finish(false)
            // Shift (45°) and Ctrl (snapping) take effect at once, without waiting for a move
            else if (event.key === "Shift" || event.key === "Control")
                preview(
                    gesture,
                    gesture.lastClientX,
                    gesture.lastClientY,
                    event.shiftKey,
                    event.ctrlKey
                )
        }
        window.addEventListener("keydown", handleKey)
        window.addEventListener("keyup", handleKey)
        return () => {
            window.removeEventListener("keydown", handleKey)
            window.removeEventListener("keyup", handleKey)
        }
    })

    // unmounted mid-gesture (selection changed): never leave a preview or a locked document
    useEffect(
        () => () => {
            if (gestureRef.current) restoreGesture(gestureRef.current)
        },
        []
    )

    const length = Math.hypot(end.x - start.x, end.y - start.y)
    const angle = (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI
    const handleSize = CORNER_SIZE_PX / scale
    const color = theme.palette.primary.light

    return (
        <>
            {/* the line itself is the outline — a box around a diagonal line says nothing */}
            <div
                style={{
                    position: "absolute",
                    left: start.x,
                    top: start.y,
                    width: length,
                    height: 0,
                    borderTop: `${1 / scale}px solid ${color}`,
                    transform: `translateY(${-0.5 / scale}px) rotate(${angle}deg)`,
                    transformOrigin: "0 0",
                }}
            />
            {interactive &&
                [start, end].map((point, index) => (
                    <div
                        key={index}
                        {...{ [RESIZE_HANDLE_ATTRIBUTE]: "" }}
                        onPointerDown={handlePointerDown(index as 0 | 1)}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={() => finish(false)}
                        onLostPointerCapture={() => finish(true)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: "absolute",
                            left: point.x - handleSize / 2,
                            top: point.y - handleSize / 2,
                            width: handleSize,
                            height: handleSize,
                            boxSizing: "border-box",
                            backgroundColor: theme.palette.common.white,
                            border: `${1 / scale}px solid ${color}`,
                            cursor: "crosshair",
                            pointerEvents: "auto",
                            touchAction: "none",
                        }}
                    />
                ))}
        </>
    )
}

export default LineHandles
